import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { getAttributeInfo } from '../../helpers/getAttributeInfo';

import {
    useItemTranslations,
    INTERFACE_STATE,
} from '../../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

// components
import { FormattedMessage } from 'dibs-react-intl';
import TranslationToggle from 'dibs-elements/exports/TranslationToggle';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import { trackTranslationToggle } from '../../tracking/sharedTranslationToggleTracking';

// styles
import styles from './PdpRespSharedItemDetailsDescription.scss';

// types
import { PdpRespSharedItemDetailsDescription_item$key as Item } from './__generated__/PdpRespSharedItemDetailsDescription_item.graphql';

export const descriptionTitle = (
    <FormattedMessage id="PdpRespSharedAboutItemDescription.header" defaultMessage="About" />
);

type Props = {
    item: Item;
    title?: ReactNode;
    lineClamp?: number;
    headerClass?: string;
    wrapperClass?: string;
    contentClass?: string;
    showExpandingArea?: boolean;
};

type ContentProps = Pick<Props, 'contentClass' | 'lineClamp' | 'showExpandingArea'> & {
    showOriginal: boolean;
    collapsedHeight: number;
    description?: string | null;
    englishDescription?: string | null;
};

export const Content: FC<ContentProps> = ({
    description,
    showOriginal,
    contentClass,
    collapsedHeight,
    englishDescription,
    showExpandingArea = true,
}) => {
    if (!description) {
        return null;
    }
    const descriptionToShow = showOriginal ? englishDescription : description;

    const content = (
        <span
            data-tn="pdp-item-description-content"
            className={classNames(styles.content, contentClass)}
            dangerouslySetInnerHTML={{ __html: (descriptionToShow || '').trim() }}
        />
    );
    return (
        <>
            {showExpandingArea ? (
                <ExpandingAreaWithReadMore
                    showGradient={false}
                    collapsedHeight={collapsedHeight}
                    wrapperClass={styles.expandingAreaWrapper}
                >
                    {content}
                </ExpandingAreaWithReadMore>
            ) : (
                content
            )}
        </>
    );
};

const itemQuery = graphql`
    fragment PdpRespSharedItemDetailsDescription_item on Item {
        seoDescription
        isCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
    }
`;

export const PdpRespSharedItemDetailsDescription: FC<Props> = ({
    headerClass,
    item: itemRef,
    showExpandingArea,
    title = descriptionTitle,
}) => {
    const [state, dispatch] = useItemTranslations();
    const item = useFragment(itemQuery, itemRef);
    const { isCustomizable, seoDescription } = item;
    const { customizationOptions, isResizable } = getAttributeInfo(item?.attributes);

    let showCustomizationOptions = true;
    if ((!isCustomizable || !customizationOptions) && !isResizable) {
        showCustomizationOptions = false;
    }
    const collapsedHeight = showCustomizationOptions ? 6 : 8;

    if (!seoDescription) {
        return null;
    }

    return (
        <div data-tn="pdp-item-description">
            <div className={styles.headerContainer}>
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            dataTn="pdp-item-description-title"
                            className={classNames(styles.header, headerClass)}
                        >
                            {title}
                        </Heading>
                    )}
                </HeadingLevel>
                {state && dispatch && (
                    <TranslationToggle
                        currentState={state.interfaceState}
                        className={styles.translationToggleContainer}
                        onClick={() => {
                            trackTranslationToggle();
                            dispatch({ type: 'TOGGLE_TRANSLATIONS' });
                        }}
                    />
                )}
            </div>
            <Content
                collapsedHeight={collapsedHeight * 30}
                description={item.seoDescription}
                showExpandingArea={showExpandingArea}
                englishDescription={state?.data?.viewer?.item?.seoDescription}
                showOriginal={state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL}
            />
        </div>
    );
};
